(ns hai.notes.card
  "The note card pieces every notes surface shares: the save-status /
   shared-with footer with its sharing dialog, and the compact
   read-only card surface list views render."
  (:require
   [hai.entity.kebab :as kebab]
   [hai.notes.form :as form]
   [hai.sharing.components :as sharing.components]
   [hai.ui.components :as c]
   [hai.ui.components.collection.views.table :as table]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [<s]]
   [hai.ui.tokens :as t]
   [hai.ui.utils.time :as time]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]))

(def ^:private share-icon (icons/mui-icon :outlined :person_add))

(o/defstyled note-footer-styled :div
  {:display "flex" :flex-direction "column" :gap "12px"}
  [:.cutoff
   {:border-top-width "1px" :border-top-style "dashed" :border-top-color t/--note-sage-35}]
  [:.footer-row
   {:display "flex" :align-items "center" :gap "10px"}]
  [:.save-status
   {:font-size "12px" :font-weight "500"
    :color t/--note-sage-80
    :margin-right "auto"}]
  [:.shared-with-label
   {:font-size "13px" :font-weight "600"
    :color t/--note-kraft}]
  [:.can-share
   {:cursor "pointer"}]
  [:.share-btn
   {:width "28px" :height "28px"
    :border "none" :border-radius "999px"
    :background "transparent"
    :padding "0"
    :color t/--note-kraft
    :cursor "pointer"
    :display "inline-flex" :align-items "center" :justify-content "center"
    :flex "0 0 auto"
    :transition "background 0.15s ease"}
   [:&:hover {:background t/--note-kraft-10}]
   [:&:disabled {:opacity "0.4" :cursor "default" :background "transparent"}]
   [:svg {:width "15px" :height "15px" :fill "currentColor"}]])

(defn share-dialog
  "Sharing dialog for a note. `!open?` controls it; closing calls
   `on-closed` so the host can re-resolve the note and surface freshly-added
   collaborators."
  [{:keys [note !open? on-closed]}]
  [c/DialogRoot
   {:open @!open?
    :onOpenChange (fn [opening?]
                    (reset! !open? opening?)
                    (when-not opening?
                      (on-closed)))}
   [c/DialogContent
    [sharing.components/sharing-dialog-title]
    [c/dialog-close-x]
    [c/DialogDescription "People with access to this note"]
    [sharing.components/permissions-table note]]])

(defn note-footer
  "Save status, who the note is shared with (the author aside), and the share
   button opening the Sharing dialog. Always rendered so the editor's
   layout doesn't jump when the first auto-save lands — the share button is
   disabled until the note exists. `on-share-opened` (optional) fires as the
   Sharing dialog opens; `on-share-closed` re-resolves the note after it
   closes. `share?` hides the share button entirely — for hosts whose viewer
   may lack edit permission on the note."
  [{:keys [note saving? on-share-opened on-share-closed share?]
    :or {share? true}}]
  (r/with-let [!share-open? (r/atom false)]
    (let [requester-uuid (:user/uuid @(<s [:auth/identity]))
          shared-with    (remove #(= requester-uuid (:user/uuid %))
                                 (:entity/shared-with note))
          open-share-dialog-fn (fn []
                                 (reset! !share-open? true)
                                 (when on-share-opened
                                   (on-share-opened)))
          updated-at     (:note/updated-at note)]
      [note-footer-styled
       [:div.cutoff]
       [:div.footer-row
        [:span.save-status
         (cond
           saving?    "Saving…"
           updated-at (str "Saved at " (time/format-saved-at updated-at)))]
        (when (seq shared-with)
          [:span.shared-with-label
           {:on-click #(when share?
                         (open-share-dialog-fn))
            :class (when share? "can-share")}
           "Shared with "
           (table/label {:items shared-with
                         :xform (map sharing.components/shared-with-label)})])
        (when share?
          [:<>
           [c/Tooltip {:content "Share this note"}
            [:button.share-btn
             {:type "button"
              :aria-label "Share this note"
              :disabled (nil? (:note/uuid note))
              :on-click open-share-dialog-fn}
             [share-icon]]]
           [share-dialog {:note      note
                          :!open?    !share-open?
                          :on-closed on-share-closed}]])]])))

(o/defstyled card-surface :div
  {:height "300px"
   :box-sizing "border-box"
   :border "1px solid transparent"
   :border-radius "4px"
   :color t/--note-kraft
   :background-color t/--sticky-yellow-light
   :box-shadow "0 1px 2px rgba(45,62,42,0.08), 0 10px 24px rgba(45,62,42,0.14)"
   :padding "12px 16px 14px"
   :display "flex" :flex-direction "column" :gap "10px"
   :white-space "pre-wrap"
   :word-break "break-word"
   :transition "border-color 0.15s ease"}
  ;; Border on hover — pointer devices only, so a tap on touch doesn't leave
  ;; the card stuck in a highlighted state. The border sits transparent at
  ;; rest so nothing shifts.
  [:at-media {:hover "hover"}
   [:&:hover
    {:border-color t/--card-forest}]]
  [:.card-actions
   {:display "flex" :justify-content "flex-end"
    :flex "0 0 auto"
    :margin "-4px -8px -4px 0"}]
  ;; The text shrinks far more readily than the chip rows below (1000× shrink
  ;; weight), but never below two lines (2 × 14px × 1.5). Once it hits that
  ;; floor, the chips clip instead — so a metadata-heavy note can't squeeze the
  ;; text out of the card, and a long note keeps its chips intact.
  [:.scroll-wrap
   {:position "relative"
    :flex "1 1000 auto" :min-height "42px"
    :display "flex" :flex-direction "column"}]
  [:.card-scroll
   {:flex "1 1 auto" :min-height "0"
    :overflow-y "auto"
    :cursor "pointer"
    :user-select "none"
    :scrollbar-width "thin"
    :scrollbar-color "rgba(74,93,63,0.4) transparent"}
   ["&::-webkit-scrollbar" {:width "6px"}]
   ["&::-webkit-scrollbar-track" {:background-color "transparent"}]
   ["&::-webkit-scrollbar-thumb" {:background-color t/--note-sage-35
                                  :border-radius "3px"}]]
  [:.body {:font-size "14px" :line-height "1.5" :font-weight "500"}]
  [:.card-fade
   {:position "absolute" :left "0" :right "0" :bottom "0"
    :height "32px"
    ;; Tokens can't be (str …)-interpolated in a pure-cljs defstyled, so the
    ;; transparent end restates --sticky-yellow-light's value by hand.
    :background "linear-gradient(to bottom, rgba(250,244,223,0), var(--sticky-yellow-light))"
    :pointer-events "none"
    :opacity "0"
    :transition "opacity 0.15s ease"}
   [:&.visible {:opacity "1"}]]
  [:.extras-clip
   {:position "relative"
    :flex "0 1 auto" :min-height "22px"
    :overflow "hidden"}]
  [note-footer-styled
   {:flex "0 0 auto"}])

(defn card
  "The compact read-only card surface — the note's text scrolling inside the
   fixed height, the tag/link chips, and the save/share footer pinned under
   it. A bottom fade shows while more text or chips sit below the fold.
   `on-body-click` fires on the text area; the ⋮ (Delete) shows when
   `on-delete` is given; `share?` gates the footer's share button; `footer?`
   drops the footer row entirely."
  [{:keys [note tags link-urls contacts linked-notes linked-tasks
           on-body-click on-delete share? footer?
           on-share-closed]
    :or   {share? true footer? true}}]
  (r/with-let [!overflow?        (r/atom false)
               !extras-overflow? (r/atom false)
               !scroll-el        (atom nil)
               !extras-el        (atom nil)
               check!     (fn []
                            (when-let [^js el @!scroll-el]
                              (reset! !overflow?
                                      (< 2 (- (.-scrollHeight el)
                                              (.-scrollTop el)
                                              (.-clientHeight el)))))
                            (when-let [^js el @!extras-el]
                              (reset! !extras-overflow?
                                      (< 2 (- (.-scrollHeight el)
                                              (.-clientHeight el))))))
               observer   (js/ResizeObserver. (fn [_] (check!)))
               observe!   (fn [!slot el]
                            (when-let [^js prev @!slot]
                              (.unobserve observer prev))
                            (reset! !slot el)
                            (when el
                              (.observe observer el)
                              (check!)))]
    [card-surface
     (when on-delete
       [:div.card-actions
        [kebab/menu
         {:on-delete      on-delete
          :delete-label   "Delete note"
          :confirm-prompt "Delete this note?"
          :aria-label     "Note actions"}]])
     [:div.scroll-wrap
      [:div.card-scroll
       {:ref       #(observe! !scroll-el %)
        :on-scroll (fn [_] (check!))
        :on-click  on-body-click}
       ;; Re-key on updated-at: the form state seeds from the note at setup, so
       ;; an edit landing from another surface must remount to show.
       ^{:key [(:note/uuid note) (:note/updated-at note)]}
       [form/note-form
        {:form-id  [::card-form (:note/uuid note)]
         :note     note
         :editing? false}]]
      [:div.card-fade {:class (when @!overflow? "visible")}]]
     (when (or (seq tags) (seq link-urls) (seq contacts)
               (seq linked-notes) (seq linked-tasks))
       [:div.extras-clip
        {:ref #(observe! !extras-el %)}
        [form/note-extras
         {:editing?     false
          :tags         tags
          :link-urls    link-urls
          :contacts     contacts
          :linked-notes linked-notes
          :linked-tasks linked-tasks}]
        [:div.card-fade {:class (when @!extras-overflow? "visible")}]])
     (when footer?
       [note-footer
        {:note            note
         :share?          share?
         :on-share-closed on-share-closed}])]
    (finally (.disconnect observer))))
